import type { FDHttpMethod, FDRequestOptions, FDResponse, FDRawHeaders } from './http-client'
import httpClient from './http-client'
import { Md5 } from 'ts-md5'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/core/services/auth-credentials'
import { useCommonData } from '@/stores/commom'
import Cookies from 'js-cookie'
// #ifdef MP-WEIXIN
import Singlestore from '@/static/js/singlestore' // 单店小程序
// #endif

export interface FDApiResult<T> {
  code: string | number
  msg?: string
  time: string
  data: T
  message?: string
  desc: string
}

interface REQData {
  lon: number
  lat: number
  device_name: string
  equipment_num: string
  build: string | number
  ftype: string
  applet_scene?: number
  applet_merchant_id?: number
  device_lat?: number
  device_lon?: number
}

export abstract class BaseApi<TReqData = any, TRespData = any>
  implements FDRequestOptions<Record<string, any>, TReqData> {
  /**
   * 超时时间
   */
  protected readonly timeout: number = 60000
  /**
   * 基础路径
   */
  protected baseUrl: string

  /**
   * 请求路径
   * */
  protected url?: string

  /**
   * 请求方法
   * */
  protected method: FDHttpMethod = 'GET'

  /**
   * query 参数
   * */
  protected query: Record<string, any> = {}

  /**
   * body
   * */
  protected data: TReqData & REQData = {} as TReqData & REQData

  /**
   * 请求头
   */
  protected headers: FDRawHeaders = {
    //'X-Requested-With':'XMLHttpRequest',
  }

  /**
   * 文件流
   */
  protected filePath?: any

  /**
   * 最后响应结果
   * */
  protected latestResponse?: FDResponse<Record<string, any>, TReqData, FDApiResult<TRespData>>

  // 签名随机数
  protected signature_nonce = ''
  protected sign_key = 'f0bc6fae953233ab'

  /**
   * 构造函数
   * */
  public constructor() {
    const { profile } = storeToRefs(useUserStore())
    const { receiveAddress, deviceAddress, lang } = storeToRefs(useCommonData())

    // this.headers.token = Cookies.get('token') || profile.value?.token || ''
    // this.headers.Authorization = Cookies.get('token') || profile.value?.token || ''
    this.headers['X-CNTY'] = 0
    this.headers['Accept-Language'] = lang.value?.value || 'en-us'
    this.baseUrl = import.meta.env.VITE_BASE_URL

    const defaultData: REQData = {
      lon: receiveAddress.value.lng,
      lat: receiveAddress.value.lat,
      device_name: uni.getSystemInfoSync().model,
      equipment_num: uni.getSystemInfoSync().deviceId,
      build: '213', // 改为新ui版本后build要改为220以上
      ftype: 'applet',
    }
    Object.assign(this.data, defaultData)

    if (deviceAddress.value?.lat) this.data.device_lat = deviceAddress.value.lat
    if (deviceAddress.value?.lng) this.data.device_lon = deviceAddress.value.lng

    this.initPlatformSpecificData()

    if (this.url?.startsWith('/apiv2'))
      this.signature_nonce = 'abcdefghijklmnopqrstuvwxyz0123456789'
  }

  protected initPlatformSpecificData() {
    const { isApp, isFlutter, appParams } = storeToRefs(useCommonData())
    const { V20241127_USER_IOS, V20241127_USER_ANDROID } = useCommonData()
    // #ifdef MP-WEIXIN
    this.data.applet_scene = 3
    if (Singlestore.selef.isSinglestore) {
      this.data.applet_scene = 6
      this.data.applet_merchant_id = Singlestore.selef.getShopId()
    } else if (Singlestore.selef.isTakeoutMiniProgram) {
      this.data.applet_scene = 1
    }
    // #endif

    // #ifdef MP-ALIPAY
    this.data.applet_scene = 5
    // #endif

    // #ifdef H5
    this.data.ftype = 'wap'
    if (isApp.value) {
      this.data.ftype = appParams.value.ftype
      this.data.build = appParams.value.build
      this.data.device_name = appParams.value.device_name
      this.data.equipment_num = appParams.value.equipment_num
    }

    if (isFlutter.value) {
      this.data.ftype = appParams.value.platform
      this.data.build =
        appParams.value.build * 1 === 0
          ? appParams.value.platform === 'android'
            ? V20241127_USER_ANDROID
            : V20241127_USER_IOS
          : appParams.value.build
      this.data.device_name = appParams.value.channel
      this.data.equipment_num = appParams.value.device_id
    }
    // #endif
  }

  /**
   * 执行请求
   * */
  public async exec(): Promise<FDApiResult<TRespData>> {
    const resp = await httpClient.request<Record<string, any>, TReqData, FDApiResult<TRespData>>(
      this as any,
    )
    this.latestResponse = resp
    return resp.data
  }

  /**
   * 签名方法
   */
  protected generateSign(data: Record<string, any>): string {
    const arr = Object.keys(data)
      .filter((key) => key !== 'sign')
      .map((key) => ({ name: key, val: data[key] }))
      .sort((a, b) => b.name.localeCompare(a.name))

    const signText =
      arr.map((item) => `${item.name}=${item.val}`).join('&') +
      '&' +
      this.signature_nonce +
      this.sign_key

    // console.log('signText:', signText)

    return Md5.hashStr(signText)
  }
}

export abstract class BaseGetApi<TRespData = any> extends BaseApi<any, TRespData> {
  constructor() {
    super()
    const { profile } = storeToRefs(useUserStore())
    this.headers['content-type'] = 'application/json'
    // this.data.uid = Cookies.get('uid') || profile.value?.uid || ''
    // this.data.token = Cookies.get('token') || profile.value?.token || ''
    this.data.timestamp = Date.parse(new Date().toString()) / 1000
  }
}

export abstract class BasePostApi<TRespData = any> extends BaseApi<any, TRespData> {
  protected method: FDHttpMethod = 'POST'

  constructor() {
    super()
    const { profile } = storeToRefs(useUserStore())
    this.headers['content-type'] = 'application/x-www-form-urlencoded'
    // this.data.uid = Cookies.get('uid') || profile.value?.uid || ''
    // this.data.token = Cookies.get('token') || profile.value?.token || ''
    this.data.timestamp = Date.parse(new Date().toString()) / 1000
  }

  public postExec(): Promise<FDApiResult<TRespData>> {
    this.data.sign = this.generateSign(this.data)
    return super.exec()
  }
}

// 商城
export abstract class ShopeBaseGetApi<TRespData = any> extends BaseGetApi<any> {
  public constructor() {
    super()
    this.baseUrl = import.meta.env.VITE_SHOP_BASE_URL
  }
}

export abstract class ShopeBasePostApi<TRespData = any> extends BasePostApi<any> {
  public constructor() {
    super()
    this.baseUrl = import.meta.env.VITE_SHOP_BASE_URL
  }
}

// 图片上传
export abstract class UploadBaseApi<TReqData = any, TRespData = any> extends BasePostApi<any> {
  /**
   * 执行请求
   * */
  public async uploadExec(): Promise<any> {
    const { profile } = storeToRefs(useUserStore())
    // this.data.uid = Cookies.get('uid') || profile.value?.uid || ''
    // this.data.token = Cookies.get('token') || profile.value?.token || ''
    this.data.timestamp = Date.parse(new Date().toString()) / 1000
    this.data = {
      ...this.data,
      sign: this.generateSign(JSON.parse(JSON.stringify(this.data))),
    }

    const resp = await httpClient.upload<Record<string, any>, TReqData, FDApiResult<TRespData>>(
      this as any,
    )
    this.latestResponse = resp
    return resp
  }
}

// 获取公共参数
export class publicParamsApi extends BasePostApi {
  protected url = '/api/Takeout/publicParams'

  private static cache?: FDApiResult<any>
  set setParams(obj: object) {
    this.data = {
      ...this.data,
      ...obj,
    }
  }

  public async getPublicParams() {
    if (publicParamsApi.cache) return publicParamsApi.cache
    const result = await super.postExec()
    publicParamsApi.cache = result
    return result
  }
}

// apiv1鉴权含body参数请求
export abstract class Apiv1BodyRequest<TReqData = any, TRespData = any> extends BasePostApi<any> {
  protected method: FDHttpMethod = 'POST'
  protected urlParams = ''

  constructor() {
    super()
    this.headers['content-type'] =
      this.method === 'POST' ? 'application/x-www-form-urlencoded' : 'application/json'

    const querySing = this.generateSign({
      timestamp: this.data.timestamp,
      lat: this.data.lat,
      lon: this.data.lon,
      ...this.query,
    })
    const params: AnyArray = []
    for (const key in this.query) {
      params.push(`${key}=${this.query[key]}`)
    }
    this.urlParams = `timestamp=${this.data.timestamp}&signature_nonce=${this.signature_nonce}&sign=${querySing}&lat=${this.data.lat}&lon=${this.data.lon}`
    if (params.length) this.urlParams += `&${params.join('&')}`
  }

  /**
   * 执行请求
   * */
  public async bodyExec(): Promise<FDApiResult<TRespData>> {
    if (this.url !== '') this.url += `?${this.urlParams}`
    const resp = await httpClient.request<Record<string, any>, TReqData, FDApiResult<TRespData>>(
      this as any,
    )
    this.latestResponse = resp
    return resp.data
  }
}

// 含body参数请求
export abstract class BodyRequest<TReqData = any, TRespData = any> extends BasePostApi<any> {
  protected method: FDHttpMethod = 'POST'
  protected urlParams = ''

  constructor() {
    super()
    this.headers['content-type'] =
      this.method === 'POST' ? 'application/x-www-form-urlencoded' : 'application/json'
  }

  private setSign() {
    const querySing = this.generateSign({
      timestamp: this.data.timestamp,
      lat: this.data.lat,
      lon: this.data.lon,
      ...this.query,
    })

    const params: AnyArray = []
    for (const key in this.query) {
      params.push(`${key}=${this.query[key]}`)
    }

    this.urlParams = `timestamp=${this.data.timestamp}&signature_nonce=${this.signature_nonce}&sign=${querySing}&lat=${this.data.lat}&lon=${this.data.lon}`

    if (params.length) this.urlParams += `&${params.join('&')}`
  }

  /**
   * 执行请求
   * */
  public async bodyExec(): Promise<FDApiResult<TRespData>> {
    this.setSign()

    if (this.url !== '') this.url += `?${this.urlParams}`

    const resp = await httpClient.request<Record<string, any>, TReqData, FDApiResult<TRespData>>(
      this as any,
    )

    this.latestResponse = resp
    return resp.data
  }

  public async bodyUploadExec(): Promise<any> {
    this.setSign()
    if (this.url !== '') this.url += `?${this.urlParams}`
    const resp = await httpClient.upload<Record<string, any>, TReqData, FDApiResult<TRespData>>(
      this as any,
    )

    this.latestResponse = resp
    return resp
  }
}

